import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getVisitorDetails,
    deleteVisitor,
    blockVisitor,
    unblockVisitor,
} from '../../../redux/actions/SocietyPortal/VisitorAction/VisitorAction';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_VISITORS,
    SOCIETY_PORTAL_VISITORS_EDIT,
} from '../../../Routing/routes';
import BlockVisitorModal from './components/BlockVisitorModal';
import UnblockVisitorDialog from './components/UnblockVisitorDialog';
import DeleteVisitorDialog from './components/DeleteVisitorDialog';
import VisitorBlockedBanner from './components/VisitorBlockedBanner';
import VisitorProfileHeader from './components/VisitorProfileHeader';
import VisitorNotFound from './components/VisitorNotFound';
import { VisitorLoader } from './components/VisitorListStates';
import {
    VisitorActivityStrip,
    VisitorContactPanel,
    VisitorNotesPanel,
    VisitorRecordTrail,
} from './components/VisitorDetailPanels';
import { isBlocked } from './visitorConstants';
import { usePageRefresh } from '../../../context/PageRefreshContext';

const ViewVisitor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const canUpdate = can(PERMISSIONS.VMS_VISITOR_UPDATE);
    const canDelete = can(PERMISSIONS.VMS_VISITOR_DELETE);

    const {
        visitorDetailsData: visitor,
        visitorDetailsLoading,
        deleteVisitorLoading,
        blockLoading,
    } = useSelector(state => state.VisitorReducer || {});

    const [showBlock, setShowBlock] = useState(false);
    const [showUnblock, setShowUnblock] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (id) dispatch(getVisitorDetails({ id }));
    }, [dispatch, id]);

    const refresh = () => dispatch(getVisitorDetails({ id }));
    usePageRefresh(refresh);
    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS);

    const handleBlock = (reason) => {
        dispatch(blockVisitor({
            id,
            reason: reason || undefined,
            onSuccess: () => { setShowBlock(false); refresh(); },
        }));
    };

    const handleUnblock = () => {
        dispatch(unblockVisitor({
            id,
            onSuccess: () => { setShowUnblock(false); refresh(); },
        }));
    };

    const handleDelete = () => {
        dispatch(deleteVisitor({
            id,
            onSuccess: () => { setConfirmDelete(false); backToList(); },
        }));
    };

    const shell = (children) => (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">{children}</div>
    );

    if (visitorDetailsLoading) {
        return shell(<div className="tw-py-16"><VisitorLoader label="Loading visitor..." /></div>);
    }

    if (!visitor) {
        return shell(<VisitorNotFound onBack={backToList} />);
    }

    return shell(
        <div className="tw-w-full tw-space-y-4">
            <button
                onClick={backToList}
                className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
            >
                <Icon icon="mdi:arrow-left" /> Back to Visitors
            </button>

            {isBlocked(visitor) && (
                <VisitorBlockedBanner
                    visitor={visitor}
                    canUpdate={canUpdate}
                    busy={blockLoading}
                    onUnblock={() => setShowUnblock(true)}
                />
            )}

            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <VisitorProfileHeader
                    visitor={visitor}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    busy={blockLoading}
                    onBlock={() => setShowBlock(true)}
                    onEdit={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS_EDIT.replace(':id', id))}
                    onDelete={() => setConfirmDelete(true)}
                />
                <VisitorActivityStrip visitor={visitor} />
                <VisitorContactPanel visitor={visitor} />
                <VisitorNotesPanel notes={visitor.notes} />
                <VisitorRecordTrail visitor={visitor} />
            </div>

            {showBlock && (
                <BlockVisitorModal
                    visitor={visitor}
                    submitting={blockLoading}
                    onCancel={() => setShowBlock(false)}
                    onConfirm={handleBlock}
                />
            )}

            <UnblockVisitorDialog
                visitor={showUnblock ? visitor : null}
                busy={blockLoading}
                onCancel={() => setShowUnblock(false)}
                onConfirm={handleUnblock}
            />

            <DeleteVisitorDialog
                visitor={confirmDelete ? visitor : null}
                busy={deleteVisitorLoading}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default ViewVisitor;
